import { useAtom } from 'jotai';
import { filterListStateAtom } from './filterList.atom';
import { filterListStore } from './FilterListAtomProvider';
import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useInstanceId } from 'shared/src/hooks';

export const useFilterListAtom = () => {
  const instanceId = useInstanceId();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const [filterListAtomMap, setFilterListAtomMap] = useAtom(
    filterListStateAtom,
    { store: filterListStore },
  );

  // 현재 instanceId에 해당하는 상태 가져오기
  const filterListAtomValue = useMemo(
    () => filterListAtomMap[instanceId] ?? {},
    [filterListAtomMap, instanceId],
  );

  // 특정 instanceId의 상태만 업데이트하는 함수
  const setFilterListAtom = useCallback(
    (
      updater:
        | Partial<typeof filterListAtomValue>
        | ((
            prev: typeof filterListAtomValue,
          ) => Partial<typeof filterListAtomValue>),
    ) => {
      setFilterListAtomMap(prev => {
        const currentValue = prev[instanceId] ?? {};
        const newValue =
          typeof updater === 'function' ? updater(currentValue) : updater;

        return {
          ...prev,
          [instanceId]: {
            ...currentValue,
            ...newValue,
          },
        };
      });
    },
    [setFilterListAtomMap, instanceId],
  );

  /**
   * - 특정 instanceId의 상태를 교체하는 함수 (merge 없이 전체 교체)
   * - route params가 변경될 때 이전 스크린의 stale 값을 제거하기 위해 사용
   */
  const replaceFilterListAtom = useCallback(
    (newValue: Partial<typeof filterListAtomValue>) => {
      setFilterListAtomMap(prev => ({
        ...prev,
        [instanceId]: newValue as typeof filterListAtomValue,
      }));
    },
    [setFilterListAtomMap, instanceId],
  );

  /**
   * - filterListAtom의 search_keyword 초기화
   */
  const clearSearchKeyword = useCallback(() => {
    navigation.setParams({ search_keyword: undefined });
    setFilterListAtom(prev => ({
      ...prev,
      search_keyword: undefined,
    }));
  }, [setFilterListAtom, navigation]);

  /**
   * - 현재 instanceId의 filterListAtom을 초기화
   */
  const clearFilterList = useCallback(() => {
    setFilterListAtomMap(prev => ({
      ...prev,
      [instanceId]: {},
    }));
  }, [setFilterListAtomMap, instanceId]);

  /**
   * - 칩 필터만 초기화 (라디오 필터는 보존)
   */
  const clearChipFilters = useCallback(
    (radioQueryName?: string) => {
      setFilterListAtomMap(prev => {
        const currentValue = prev[instanceId] ?? {};
        const preserved: Record<string, unknown> = {};
        if (currentValue.screen_name !== undefined) {
          preserved.screen_name = currentValue.screen_name;
        }
        if (
          radioQueryName &&
          currentValue[radioQueryName] !== undefined
        ) {
          preserved[radioQueryName] = currentValue[radioQueryName];
        }
        return {
          ...prev,
          [instanceId]: preserved,
        };
      });
    },
    [setFilterListAtomMap, instanceId],
  );

  /**
   * - 오늘출발 칩과 같이 type이 toggle인 경우 사용
   */
  const setToggleFilterChip = useCallback(
    ({
      queryName,
      queryValue,
      isSelected,
    }: {
      queryName: string;
      queryValue: string;
      isSelected: boolean;
    }) => {
      const nextValue = { [queryName]: isSelected ? undefined : queryValue };
      setFilterListAtom(prev => ({
        ...prev,
        ...nextValue,
      }));
      navigation.setParams(nextValue);
    },
    [setFilterListAtom, navigation],
  );

  const setRadioFilter = useCallback(
    ({
      queryName,
      queryValue,
    }: {
      queryName: string;
      queryValue: string;
    }) => {
      const nextValue = { [queryName]: queryValue };
      setFilterListAtom(prev => ({
        ...prev,
        ...nextValue,
      }));
      navigation.setParams(nextValue);
    },
    [setFilterListAtom, navigation],
  );

  const setSearchKeyword = useCallback(
    (searchKeyword: string) => {
      navigation.setParams({ search_keyword: searchKeyword });

      setFilterListAtom(prev => ({
        ...prev,
        search_keyword: searchKeyword,
      }));
    },
    [setFilterListAtom, navigation],
  );
  return {
    clearSearchKeyword,
    clearFilterList,
    clearChipFilters,
    filterListAtomValue,
    replaceFilterListAtom,
    setRadioFilter,
    setToggleFilterChip,
    setSearchKeyword,
    setFilterListAtom,
  };
};
